import React, { useState, useEffect, useRef, useCallback } from 'react';
import Spinner from './Spinner';
import './LazyImage.css';

const LazyImage = ({
  src,
  alt,
  className = '',
  style = {},
  showSpinner = true,
  spinnerSize = 'medium',
  onLoad,
  onError,
  fallbackSrc = 'https://via.placeholder.com/400x300?text=Image+Not+Found',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Handle intersection observer
  useEffect(() => {
    let observer;
    const currentContainer = containerRef.current;

    if (currentContainer && !isInView) {
      if (typeof IntersectionObserver !== 'undefined') {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting || entry.intersectionRatio > 0) {
                setIsInView(true);
                observer.unobserve(currentContainer);
              }
            });
          },
          {
            threshold: 0.01,
            rootMargin: '50px',
          }
        );
        observer.observe(currentContainer);
      } else {
        // Fallback for older browsers
        setIsInView(true);
      }
    }

    return () => {
      if (observer && currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, [isInView]);

  // Load image when in view
  useEffect(() => {
    if (isInView && src && !currentSrc) {
      setCurrentSrc(src);
      setIsLoading(true);
    }
  }, [isInView, src, currentSrc]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setIsLoading(false);
    setHasError(false);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    if (!hasError && fallbackSrc && currentSrc !== fallbackSrc) {
      // Try fallback image
      setCurrentSrc(fallbackSrc);
      setHasError(true);
    } else {
      // Final error state
      setHasError(true);
      setIsLoaded(false);
    }
    if (onError) onError();
  }, [hasError, fallbackSrc, currentSrc, onError]);

  // Reset states when src changes
  useEffect(() => {
    if (src !== currentSrc && isInView) {
      setIsLoaded(false);
      setHasError(false);
      setIsLoading(true);
      setCurrentSrc(src);
    }
  }, [src, currentSrc, isInView]);

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: style.backgroundColor || 'transparent',
    minHeight: style.minHeight || style.height || '200px',
    minWidth: style.minWidth || style.width || '200px',
    position: 'relative',
    overflow: 'hidden',
    ...style
  };

  // Not in view yet - show placeholder
  if (!isInView) {
    return (
      <div
        ref={containerRef}
        className={`lazy-image-placeholder ${className}`}
        style={containerStyle}
      >
        {/* Empty placeholder */}
      </div>
    );
  }

  // In view but loading or has error
  if (isLoading || (!isLoaded && !hasError)) {
    return (
      <div
        ref={containerRef}
        className={`lazy-image-loading ${className}`}
        style={containerStyle}
      >
        {showSpinner && <Spinner size={spinnerSize} />}
        {currentSrc && (
          <img
            ref={imageRef}
            src={currentSrc}
            alt={alt}
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onLoad={handleLoad}
            onError={handleError}
            loading="lazy"
          />
        )}
      </div>
    );
  }

  // Error state with no fallback
  if (hasError && !isLoaded) {
    return (
      <div
        ref={containerRef}
        className={`lazy-image-error ${className}`}
        style={{
          ...containerStyle,
          backgroundColor: '#f0f0f0',
          color: '#666',
          fontSize: '14px',
          textAlign: 'center'
        }}
      >
        <div>
          <div>⚠️</div>
          <div>Image not available</div>
        </div>
      </div>
    );
  }

  // Successfully loaded
  return (
    <div
      ref={containerRef}
      className={`lazy-image-container ${className}`}
      style={{ ...style, position: 'relative', overflow: 'hidden' }}
    >
      <img
        ref={imageRef}
        src={currentSrc}
        alt={alt}
        className={`lazy-image loaded`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        {...props}
      />
    </div>
  );
};

export default LazyImage; 